/**
 * Goal view helpers (rules v2), free of Phaser so they are unit-tested in node. ClearGoal
 * ("Closing time"): the ticket still pays and drives bagLock / lockKey, but the round is won only
 * when every item has left the board (and the bag is empty). CollectGoal ("Golden items"): the
 * round is won when the ticket is complete and every golden board piece has popped; the golden
 * marker belongs to the piece (a shuffle moves items under it, the marker stays). Core decides all
 * of it; these only read its compiled level, state and events.
 */
import { goalSetsRemaining, goldenLeft, isOrderComplete, itemsOnBoard, setsRemaining, type CompiledLevel, type Round, type RoundEvent } from '@mp/core';

/** Is this a clear-goal level (the HUD shows the items-left counter)? */
export const isClearGoal = (r: Round): boolean => r.L.clear;

/**
 * The items-left counter: items still on the board (tiles, conveyor and dispenser items; crates
 * are not items), and whether the ticket is already done. Null on an order-goal level.
 */
export function clearGoalHud(r: Round): { left: number; orderDone: boolean } | null {
  if (!r.L.clear) return null;
  return { left: itemsOnBoard(r), orderDone: isOrderComplete(r) };
}

/** Is this a collect-goal level (golden tiles on the board, the golden-items counter on the ticket)? */
export const isCollectGoal = (r: Round): boolean => r.L.collect;

/** collectGoal: piece i wears the golden marker (core's per-piece flag: it never moves with an item). */
export const isGoldenPiece = (L: CompiledLevel, i: number): boolean => L.golden[i] === 1;

/**
 * The golden-items counter: golden pieces not yet popped (one in the bag still counts), and
 * whether the ticket is already done. Null off a collect-goal level.
 */
export function collectGoalHud(r: Round): { left: number; orderDone: boolean } | null {
  if (!r.L.collect) return null;
  return { left: goldenLeft(r), orderDone: isOrderComplete(r) };
}

/** collectGoal: the golden pieces a batch popped (its `golden` events' tiles). */
export function goldenPopped(events: readonly RoundEvent[]): number[] {
  return events.flatMap((e) => (e.type === 'golden' ? e.tiles : []));
}

/**
 * The `orderDone` of a batch that the scene celebrates on its own (stamp, banner, counter), or
 * null. When the same action also meets the goal (clears the board, or pops the last golden
 * piece), `won` follows in the same list and the win takes over: no "now clear the counter" or
 * "now pop the golden items" moment.
 */
export function orderDoneOf(events: readonly RoundEvent[]): Extract<RoundEvent, { type: 'orderDone' }> | null {
  let done: Extract<RoundEvent, { type: 'orderDone' }> | null = null;
  for (const e of events) {
    if (e.type === 'orderDone') done = e;
    else if (e.type === 'won') return null;
  }
  return done;
}

/**
 * Sets the round's goal still needs, as rescue eligibility counts them: ticket sets on an order
 * level, sets still on the board on a clear-goal level, ticket sets plus golden pieces not yet
 * popped on a collect-goal level. Rescue analytics use this so their numbers match core's
 * eligibility.
 */
export const rescueSets = (r: Round): number => goalSetsRemaining(r);

/**
 * What the rescue sheet says is left: the goal's sets as rescueSets counts them, except on a
 * collect-goal level, where the ticket sets and the golden items are told apart (`golden` is null
 * on every other level).
 */
export function rescueLeft(r: Round): { sets: number; golden: number | null } {
  if (!r.L.collect) return { sets: rescueSets(r), golden: null };
  return { sets: setsRemaining(r), golden: goldenLeft(r) };
}
